import { FilterOperators } from "@crispico/foundation-gwt-js";
import {
    ADD,
    apolloClient,
    ColumnDefinition,
    createSliceFoundation,
    EditMode,
    EntityEditorPage, EntityTablePage,
    EntityTablePagePartialProps,
    EntityTablePageReducers,
    getBaseImpures,
    ITableActionParamForRun,
    Optional,
    Organization,
    PropsFrom, SliceEntityEditorPage, sliceEntityEditorPageOnlyForExtension, Utils
} from "@crispico/foundation-react";
import { AppMetaTempGlobals } from "@crispico/foundation-react/AppMetaTempGlobals";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { TabRouterPane } from "@crispico/foundation-react/components/TabbedPage/TabbedPage";
import { addEntityDescriptor, EntityDescriptor, FieldDescriptor } from "@crispico/foundation-react/entity_crud/EntityDescriptor";
import { DatePickerFieldEditor } from "@crispico/foundation-react/entity_crud/fieldEditors/DatePickerFieldEditor";
import { AssociationFieldRenderer } from "@crispico/foundation-react/entity_crud/fieldRenderers/AssociationFieldRenderer";
import DateFieldRenderer from "@crispico/foundation-react/entity_crud/fieldRenderers/DateFieldRenderer";
import StringFieldRenderer from "@crispico/foundation-react/entity_crud/fieldRenderers/StringFieldRenderer";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import { FindByFilterParams } from "@crispico/foundation-react/entity_crud/FindByFilterParams";
import { ShareLinkLogic } from "@crispico/foundation-react/entity_crud/ShareLinkLogic";
import { EntityToTagFieldDescriptor } from "@crispico/foundation-react/pages/EntityToTag/entityToTagDescriptor";
import { ReduxReusableComponents, RRCProps } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { VIDEO_SHOW_PAGE } from "app";
import { RealTimeMapRRC } from "components/realTimeMap/RealTimeMap";
import gql from "graphql-tag";
import { Location } from "history";
import _, { uniqueId } from "lodash";
import moment from "moment-timezone";
import { MissionTotalTimeFieldDescriptor } from "pages/CommonFieldDescriptors";
import { GanttUtils } from "pages/gantt/GanttUtils";
import { TIME_HOUR_OFFSET } from "pages/gantt/XopsGantt";
import React, { ReactNode } from "react";
import { EquipmentResourceUtils, TerritoriesFieldDescriptor, TerritoriesFieldRenderer } from "./EquipmentResourceUtils";
import { VideoPageRRC } from "./VideoPage";
import { Group, Item, RowLayer } from '@crispico/react-timeline-10000';
import { getLayerStyle } from "pages/gantt/ganttLayerStyles";
import { ProposedTimeSlotEntityDescriptor } from "./ProposedTimeSlotEntityDescriptor";
import { XopsGanttPartialProps, EntityTablePageWithGantt, EntityTablePageWithGanttOptions, EntityTablePageWithGanttState } from "pages/gantt/components/tables/EntityTablePageWithGantt";
import { Mission } from "types";
import { IAction } from "@crispico/react-timeline-10000/types/components/ContextMenu/IAction";
import { ContextMenuActionsProvider } from "components/ContextMenuRegistry";
import lodash from "lodash";
import { getEntityGraphFields } from "@crispico/foundation-react/utils/GraphqlUtils";
import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";

export const MOBILE_DEVICE_TYPE: string = "MOBILE_DEVICE";
export enum TableMode { TABBED_MODE = "tabbedMode", SPLIT_MODE = "splitMode" };

const FIELDS_FROM_EQUIPMENT_RESOURCE_DATA = ["fleetName", "fleetOwner", "humanResourceRFID"];
const TEN_MINUTES_MS = 10 * 60 * 1000;
const THIRTY_MINUTES_MS = 30 * 60 * 1000;

export class EquipmentResourceEntityDescriptor extends EntityDescriptor {
    constructor() {
        super({
            name: "EquipmentResource",
            miniFields: ["identifier"],
            defaultSort: { field: "updated", direction: "DESC" },
            icon: "truck",
            hasAttachedDashboards: true
        });
    }


    getGraphQlFieldsToRequest(fields?: string[]): string {
        // added equipment type, since it's used by the `Display the busiest slot per equipment type` feature
        return super.getGraphQlFieldsToRequest(fields) + " equipmentType { id }";
    }

    renderTable() {
        return <EquipmentResourceTablePageRRC {...super.renderTable().props} ref={this.entityTablePage} />;
    }
}

export const equipmentResourceEntityDescriptor = addEntityDescriptor(new EquipmentResourceEntityDescriptor()
    .isInDefaultColumnConfig(true, "identifier", "astusId", "equipmentType", "equipmentModel", "unit", "organization", "available", "updated", "territoriesName", "detectedBy",
        "engineHours", "odometer", "plateNumber", "closestAddress", "imei", "territories", "enteredTerritories", "exitedTerritories")
    .removeFieldDescriptors("fieldsLastUpdate")
    .doForFields(FIELDS_FROM_EQUIPMENT_RESOURCE_DATA, fd => fd.filterable = false)
    .doForFields(FIELDS_FROM_EQUIPMENT_RESOURCE_DATA, fd => fd.sortable = false)
    .addFieldDescriptor({ name: "identifier", type: FieldType.string, additionalFieldRendererProps: FieldDescriptor.castAdditionalFieldRendererProps(StringFieldRenderer, { asLink: true }) })
    .addFieldDescriptor({ name: "equipmentType", type: "EquipmentType" })
    .addFieldDescriptor({ name: "organization", type: "Organization" })
    .addFieldDescriptor(new EntityToTagFieldDescriptor())
    .addFieldDescriptor({
        name: "updated",
        type: FieldType.date,
        icon: "database",
        additionalFieldEditorProps: FieldDescriptor.castAdditionalFieldEditorProps(DatePickerFieldEditor, { format: Utils.dateTimeWithSecFormat }),
        additionalFieldRendererProps: FieldDescriptor.castAdditionalFieldRendererProps(DateFieldRenderer, { format: Utils.dateTimeWithSecFormat })
    })
    .addFieldDescriptor({ name: "detectedBy", type: "EquipmentResource" })
    .addFieldDescriptor({ name: "engineHours", type: FieldType.double, icon: "time" })
    .addFieldDescriptor({ name: "odometer", type: FieldType.double, icon: "flag checkered" })
    .addFieldDescriptor({ name: "plateNumber", type: FieldType.string, icon: "tag" })
    .addFieldDescriptor({ name: "closestAddress", type: "Address", icon: "map signs" })
    .addFieldDescriptor({ name: "territories", enabled: false }, new TerritoriesFieldDescriptor("territories"))
    .addFieldDescriptor({ name: "enteredTerritories", enabled: false }, new TerritoriesFieldDescriptor("enteredTerritories"))
    .addFieldDescriptor({ name: "exitedTerritories", enabled: false }, new TerritoriesFieldDescriptor("exitedTerritories"))
    .addFieldDescriptor({ name: "engineState", type: FieldType.dropdown, icon: "key" })
    .addFieldDescriptor({ name: "status", type: FieldType.dropdown, icon: "play circle" })
    .addFieldDescriptor(new MissionTotalTimeFieldDescriptor())
    .addFieldDescriptor({ name: "missions", type: FieldType.oneToMany("Mission2"), oneToManyOppositeField: "equipmentResource", enable: false, filterable: false, sortable: false, isInDefaultColumnConfig: false, clientOnly: true })
);

export interface EquipmentResourceTablePageOptions extends EntityTablePageWithGanttOptions {
    showBusiestSlotsPerEquipmentType: boolean
}

const additionalDateFieldEditorProps = FieldDescriptor.castAdditionalFieldEditorProps(DatePickerFieldEditor, { format: Utils.dateFormat });
const additionalDateFieldRendererProps = FieldDescriptor.castAdditionalFieldRendererProps(DateFieldRenderer, { format: Utils.dateFormat });

const equipmentResourceTablePageOptionsEntityDescriptor = new EntityDescriptor({ name: "EquipmentResourceTablePageOptions" })
    .addFieldDescriptor({ name: "showGantt", type: FieldType.boolean })
    .addFieldDescriptor({ name: "compactMode", type: FieldType.boolean })
    .addFieldDescriptor({ name: "date", type: FieldType.date, additionalFieldEditorProps: additionalDateFieldEditorProps, additionalFieldRendererProps: additionalDateFieldRendererProps })
    .addFieldDescriptor({ name: "showBusiestSlotsPerEquipmentType", type: FieldType.boolean });

const equipmentResourceTablePageOptionsEntityDescriptorEmbedded = new EntityDescriptor({ name: "EquipmentResourceTablePageOptions" })
    .addFieldDescriptor({ name: "showGantt", type: FieldType.boolean })
    .addFieldDescriptor({ name: "compactMode", type: FieldType.boolean })
    .addFieldDescriptor({ name: "date", type: FieldType.date, enabled: false, additionalFieldEditorProps: additionalDateFieldEditorProps, additionalFieldRendererProps: additionalDateFieldRendererProps })
    .addFieldDescriptor({ name: "showBusiestSlotsPerEquipmentType", type: FieldType.boolean });

export class EquipmentResourceTablePageState extends EntityTablePageWithGanttState<EquipmentResourceTablePageOptions> {
    protected static readonly defaultOptions: EquipmentResourceTablePageOptions = {
        ...EntityTablePageWithGanttState.defaultOptions,
        showBusiestSlotsPerEquipmentType: false,
    };
    protected static readonly localStorageKey: string = 'EquipmentResourceTablePage.options';

    missionsByERId: { [erId: number]: Mission[] } = {};
}
export class EquipmentResourceTablePageReducers<S extends EquipmentResourceTablePageState = EquipmentResourceTablePageState> extends EntityTablePageReducers<S> {
}

export type EquipmentResourceTablePageProps = { getFullEntity?: (entityName: string, entity: any) => any } & EntityTablePagePartialProps & RRCProps<EquipmentResourceTablePageState, EquipmentResourceTablePageReducers>;

export class EquipmentResourceTablePage<P extends EquipmentResourceTablePageProps = EquipmentResourceTablePageProps> extends EntityTablePageWithGantt<P> {

    static defaultProps = {
        ...EntityTablePage.defaultProps,
        allowEmptyMissionDate: false
    }

    protected getExtraTabPanes(): (TabRouterPane | null)[] {
        const that = this;
        let extraTabPanes = [...super.getExtraTabPanes(), {
            routeProps: { path: "/realTimeMap" },
            menuItemProps: { icon: "map outline", content: _msg("MapRealTime") },
            render: () => (<>
                {that.renderRealTimeMap(false, true, undefined)}
            </>)
        }];
        return extraTabPanes;
    }

    async componentDidUpdateInternal(prevProps?: P) {
        super.componentDidUpdateInternal(prevProps);
        if (prevProps && !lodash.isEqual(prevProps.s.loaded, this.props.s.loaded) && this.props.s.loaded !== -1) {
            this.loadMissions();
        }
    }

    protected getGanttStartEndTimes() {
        const date = this.props.s.options.date || Utils.now().toISOString();
        return [moment(date).startOf("day").add(-TIME_HOUR_OFFSET, "hour"), moment(date).endOf("day").add(TIME_HOUR_OFFSET, "hour")];
    }

    protected async loadMissions(list?: any[]) {
        if (!this.props.s.options?.showGantt) {
            return;
        }
        const entities = list || this.entityTableSimpleRef.current?.getEntities() as any[];
        const ids = entities?.map(e => e.id);
        if (Utils.isNullOrEmpty(ids)) {
            return;
        }
        const filters = [
            // TODO maybe split by 100?
            Filter.create("equipmentResource", FilterOperators.forNumber.in, ids!.join(",")),
            Filter.createComposed(FilterOperators.forComposedFilter.and, [
                Filter.create("endTime", FilterOperators.forDate.greaterThan, this.getGanttStartEndTimes()[0].toISOString()),
                Filter.create("startTime", FilterOperators.forDate.lessThan, this.getGanttStartEndTimes()[1].toISOString())
            ])
        ];
        const tasksPart = this.props.embeddedMode ? "" : " tasks { id actualStartTime actualEndTime comment taskType { name color } taskGroup { id name departure airline number date initialDate comment color groundAgent parking { id name } } }"; 
        
        const fragment = `id type { id color name } inactivityType { id color name } equipmentResource { id identifier } startTime endTime type { color } comment${tasksPart}
                        status finishedByUser color startAddress { id name } endAddress { id name } humanResource { id identifier firstName lastName } equipmentType { id name }
                        events { id creationdate generationSource stopInfo flight objectActionInfo descriptorType deleted comment type }`;
        const query = gql(`query q($params: FindByFilterParamsInput) { 
                mission2Service_findByFilter(params: $params) {
                    results {
                      ${fragment}
                    }
                }
            }`);
        // added sort by ER to not add a calculation to group the missions by ER AND sort by startTime to not sort it on client.
        const missions = (await apolloClient.query({
            query,
            variables: FindByFilterParams.create().filter(Filter.createComposed(FilterOperators.forComposedFilter.and, filters)).sorts([{ field: "equipmentResource.id", direction: "ASC" }, { field: "startTime", direction: "ASC" }]).entityGraph(getEntityGraphFields(fragment)),
            context: { showSpinner: false }
        })).data.mission2Service_findByFilter.results;

        const missionsByERId: { [erId: number]: Mission[] } = {};
        for (const m of missions as Mission[]) {
            const erId = m.equipmentResource?.id;
            if (!erId) { continue; }
            (missionsByERId[erId] = missionsByERId[erId] || []).push(m);
        }
        this.props.r.setInReduxState({ missionsByERId });
    }

    /**
     * @description Calculates and returns a set of layers that visually highlight the busiest time slots for each equipment type.
     * A "busiest slot" is defined as the time period with the highest number of concurrently used resources.
     * The function iterates through all equipment types, finds the busiest slot for each, and then generates
     * a `RowLayer` for every resource of that type, spanning the duration of the busiest slot.
     * These layers can be used to visually represent peak usage times on a timeline or calendar.
     */
    protected getBusiestSlotPerEquipmentTypeLayers(items: (Item & { entity: any })[]): RowLayer[] {
        const layers: RowLayer[] = [];
        const entities = this.entityTableSimpleRef.current?.getEntities();

        if (items.length === 0 || !entities) {
            return layers;
        }

        const resourcesGroupedByEquipmentType: { [key: string]: any[] } = _.groupBy(entities, "equipmentType.id");

        const itemsGroupedByEquipmentType = _.groupBy(_.filter(items, item => !String(item.key).startsWith("ProposedTimeSlot")), "entity.equipmentType.id");

        Object.keys(resourcesGroupedByEquipmentType).forEach(equipmentTypeId => {
            const resourceGroupedByEquipmentType = resourcesGroupedByEquipmentType[equipmentTypeId];
            const itemsByEquipmentType = itemsGroupedByEquipmentType[equipmentTypeId];

            if (Utils.isNullOrEmpty(itemsByEquipmentType)) {
                return;
            }

            // Find the overall time range (min start and max end) for items of the current equipment type.
            const minStart = moment(_.minBy(itemsByEquipmentType, 'start')?.start).valueOf();
            const maxStart = moment(_.maxBy(itemsByEquipmentType, 'end')?.end).valueOf();

            if (!minStart || !maxStart) {
                return;
            }

            const slotsAndResourcesSize: [number, number][] = [];

            // Iterate through time slots (e.g., every 10 minutes) within the overall time range.
            for (let startSlot = minStart; startSlot < maxStart; startSlot += TEN_MINUTES_MS) {
                const endSlot = startSlot + THIRTY_MINUTES_MS; // Each slot has a 30-minute duration.
                const resources = new Set<number>(); // Use a Set to count unique resources.

                // Check which items overlap with the current time slot.
                itemsByEquipmentType.forEach((item: (Item & { entity: any })) => {
                    const startItem = item.start as number;
                    const endItem = item.end as number;

                    if (startItem < endSlot && endItem > startSlot) {
                        resources.add(item.row);
                    }
                });
                // Store the slot's start time and the number of unique resources.
                slotsAndResourcesSize.push([startSlot, resources.size]);
            }

            // Find the slot with the highest number of concurrent resources.
            const mostBusiestSlot = _.maxBy(slotsAndResourcesSize, ([, value]) => value);

            if (!mostBusiestSlot) {
                return;
            }

            const index = slotsAndResourcesSize.indexOf(mostBusiestSlot!);
            const equipmentCount = mostBusiestSlot![1];

            // Expand the busiest slot's range to include all contiguous slots with the same peak resource count.
            let startIndex = index;
            while (startIndex > 0 && slotsAndResourcesSize[startIndex - 1][1] === equipmentCount) {
                startIndex--;
            }
            // Determine the end index of the 3-slot (30-minute) range, ensuring it doesn't exceed the array bounds.
            let endIndex = Math.min(index + (THIRTY_MINUTES_MS / TEN_MINUTES_MS) - 1, slotsAndResourcesSize.length - 1);
            while (endIndex < slotsAndResourcesSize.length - 1 && slotsAndResourcesSize[endIndex + 1][1] === equipmentCount) {
                endIndex++;
            }

            const start = slotsAndResourcesSize[startIndex][0];
            const end = slotsAndResourcesSize[endIndex][0] + THIRTY_MINUTES_MS;

            resourceGroupedByEquipmentType.forEach(resource => {
                layers.push({
                    rowNumber: resource._index,
                    start,
                    end,
                    style: getLayerStyle("BusiestSlot", null),
                });
            });
        });

        return layers;
    }

    protected getFullMission(m: Mission) {
        return this.props.getFullEntity ? this.props.getFullEntity("Mission", m) : m;
    }

    protected getTimelineData(): { groups: Group[], items: (Item & { entity: any })[], rowLayers: RowLayer[] } {
        const groups: Group[] = [];
        const items: (Item & { entity: any })[] = [];
        let rowLayers: RowLayer[] = [];
        const entities = this.entityTableSimpleRef.current?.getEntities();
        entities?.forEach(entity => {
            groups.push({ id: entity.id });
            const missions = (this.props.s as EquipmentResourceTablePageState).missionsByERId?.[entity.id] || [];
            missions.forEach((m: Mission) => {
                items.push({
                    key: GanttUtils.toEntityUid("Mission2", m.id),
                    row: m.equipmentResource.id,
                    start: m.startTime,
                    end: m.endTime,
                    entity: this.getFullMission(m)
                });
            });
        });

        items.push(...ProposedTimeSlotEntityDescriptor.getProposedTimeSlots(Object.values(this.props.s.missionsByERId || {}).flat()));

        if (this.props.s.options.showBusiestSlotsPerEquipmentType) {
            rowLayers = this.getBusiestSlotPerEquipmentTypeLayers(items);
        }
        return { groups, items, rowLayers };
    }

    protected renderRealTimeMap(showGoToTableButton: boolean = true, withCustomQueryBar: boolean = true, rootFilter: Filter | undefined) {
        // Q: uniqueId("map-tab-" + EQUIPMENT_RESOURCE_TYPE) why is this neeeded ?
        // A: RealTimeMap contains MapContainerLeaflet which is RRC so uniqueId is needed because if the entity has dashboards
        // the component will be mounted once and once more after dashboards are loaded on changing order tabs
        const id = uniqueId("realTimeMap-tab-" + equipmentResourceEntityDescriptor.name);
        return <RealTimeMapRRC id={id}
            rootFilter={rootFilter} showCustomQueryBar={withCustomQueryBar} showGoToTableButton={showGoToTableButton} mapId={id} entityTypes={[equipmentResourceEntityDescriptor.name]} />
    }

    protected getOptionsEntityDescriptor(): EntityDescriptor {
        return this.props.embeddedMode ? equipmentResourceTablePageOptionsEntityDescriptorEmbedded : equipmentResourceTablePageOptionsEntityDescriptor;
    }

    protected getXopsGanttProps(): XopsGanttPartialProps {
        const { groups, items, rowLayers } = this.getTimelineData();
        return {
            startDate: this.getGanttStartEndTimes()[0].valueOf(),
            endDate: this.getGanttStartEndTimes()[1].valueOf(),
            groups, items, rowLayers,
        }
    }
}

export const EquipmentResourceTablePageRRC = ReduxReusableComponents.connectRRC(EquipmentResourceTablePageState, EquipmentResourceTablePageReducers, EquipmentResourceTablePage);

export const sliceEntityEquipmentResource = equipmentResourceEntityDescriptor.infoEditor.slice = createSliceFoundation(class Ext extends SliceEntityEditorPage {

    getFieldsToRequest() {
        return super.getFieldsToRequest().concat(["lastPointLatitude", "lastPointLongitude", "equipmentModel.image", "equipmentType.icon", "equipmentType.isNonMotorizedEquipment",
            "equipmentModel.equipmentModelFamily.image", "equipmentModel.equipmentModelFamily.equipmentProductType.image"
        ]);
    }

    getAdditionalGraphQl() {
        const ed = this.entityDescriptor;
        const fields = Object.keys(ed.fields).filter(f => ed.fields[f].enabled && !ed.fields[f].clientOnly).map(name => "\"" + name + "\"").join(",");
        return `fieldsLastUpdate(whichFields: [${fields}])`;
    }

    nestedSlices = {
        ...sliceEntityEditorPageOnlyForExtension.nestedSlices,
        //historicalMap: sliceHistoricalMap,
    }

    impures = {
        ...sliceEntityEditorPageOnlyForExtension.impures, ...getBaseImpures<Ext>(this),

        loadSuper: sliceEntityEditorPageOnlyForExtension.impures.load,
        async load(id: typeof ADD | any, columns: Optional<ColumnDefinition[]>, currentOrganization: Optional<Organization>, location?: Location<any>) {
            // do this here to re-write queries with last version of customFields; see getFieldsToRequest()    
            // race condition for initIfNeeded and appMeta.getEntityDescriptorsForServer()   
            this.getSlice().initQueries();
            await this.loadSuper(id, columns, currentOrganization, location);
        }
    }
}).setEntityDescriptor(equipmentResourceEntityDescriptor);

equipmentResourceEntityDescriptor.infoEditor.wrappedComponentClass = class extends EntityEditorPage<PropsFrom<typeof sliceEntityEquipmentResource>> {

    protected renderPageHeaderAdditionalContent() {
        const { props } = this;
        const { entityDescriptor } = props.dispatchers.getSlice();

        return props.entity?.id && <div className="flex-container-row flex-center">
            <div className="EntityCrudHeader_white small-margin-right">
                {entityDescriptor.getField("equipmentType").renderField(props.entity, FieldDescriptor.castAdditionalFieldRendererProps(AssociationFieldRenderer, {
                    url: new ShareLinkLogic().createLink(false, entityDescriptor, Filter.enableAllFilters(Filter.create("equipmentType.name", FilterOperators.forEntityManyToOne.equals, props.entity.equipmentType?.name))),
                    showTooltip: true
                }))}
            </div>
            {entityDescriptor.getField("territories").renderField(props.entity, FieldDescriptor.castAdditionalFieldRendererProps(TerritoriesFieldRenderer, {
                linkToEquipmentResourceTableWithFilter: true,
                showTooltip: true
            }))}
        </div>
    }

    protected renderPageHeaderContent(lineWithFields: ReactNode, additionalContent?: ReactNode): ReactNode {
        const { props } = this;
        const { entityDescriptor } = props.dispatchers.getSlice();
        const equipmentModel = props.entity?.equipmentModel;
        const image = equipmentModel?.image || equipmentModel?.equipmentModelFamily?.image || equipmentModel?.equipmentModelFamily?.equipmentProductType?.image;
        return <>{super.renderPageHeaderContent(lineWithFields, this.renderPageHeaderAdditionalContent())}
            {props.entity?.id && <div className={"flex-container ER_EntityEditorPage_header_rightSide" + (equipmentModel ? (image ? "" : " ER_EntityEditorPage_header_rightSide_noImage") : " ER_EntityEditorPage_header_rightSide_noModel")}>
                {image ? <img src={image} alt={equipmentModel?.name} /> : <span></span>}
                {equipmentModel && entityDescriptor.getField("equipmentModel").renderField(props.entity, FieldDescriptor.castAdditionalFieldRendererProps(AssociationFieldRenderer, {
                    url: new ShareLinkLogic().createLink(false, entityDescriptor, Filter.enableAllFilters(Filter.create("equipmentModel.name", FilterOperators.forEntityManyToOne.equals, equipmentModel?.name))),
                    asLabel: true, showTooltip: true
                }))}
            </div>}</>
    }

    protected getHeaderIcon() {
        const props = this.props;
        return EquipmentResourceUtils.getImage(props.entity, false, '', { size: 'big' });
    }

    protected getExtraTabPanes() {
        let extraTabPanes = [...super.getExtraTabPanes()];
        extraTabPanes = extraTabPanes.slice(0, extraTabPanes.indexOf(null) + 1).concat(this.getAuditTabPanes()).concat(extraTabPanes.slice(extraTabPanes.indexOf(null) + 1));
        extraTabPanes.push(this.createOneToManyTabPane("EquipmentResource_secondaryOrgs__Organization_equipmentRes", "equipmentResource"));
        extraTabPanes.push(this.createOneToManyTabPane("Position", "plateNumber", { entityField: "plateNumber" }));
        extraTabPanes.push(this.createOneToManyTabPane("DetectionEvent", "equipmentResource"));
        if (!this.props.entity?.equipmentType || !this.props.entity.equipmentType.isNonMotorizedEquipment) {
            extraTabPanes.push(this.createOneToManyTabPane("DiagnosticTroubleCode", "equipmentResource", { filter: Filter.createComposedForClient(FilterOperators.forComposedFilter.and, []) }))
            if (AppMetaTempGlobals.appMetaInstance.hasPermission(VIDEO_SHOW_PAGE)) {
                extraTabPanes.push({
                    routeProps: { path: "/videoTab" },
                    menuItemProps: { icon: "film", content: _msg("Video") },
                    render: () => this.props.entity?.id
                        ? <VideoPageRRC id={"VideoPage_" + this.props.entity.id} equipmentResourceId={this.props.entity.id} /> : undefined
                });
            }
        }
        extraTabPanes.push({
            ...this.createOneToManyTabPane("VacKey_directEquipmentResources__EquipmentResource_directVacKeys", "equipmentResource"),
            menuItemProps: { content: _msg("EquipmentResource.vacKeyTab.label"), icon: entityDescriptors["VacKey"].icon }
        });
        extraTabPanes.push({
            ...this.createOneToManyTabPane("VacKeyGroup_equipmentResources__EquipmentResource_vacKeyGroups", "equipmentResource", {}),
            menuItemProps: { content: entityDescriptors["VacKeyGroup"].getLabel(true), icon: entityDescriptors["VacKeyGroup"].icon }
        });

        return extraTabPanes;
    }

    protected getExtraTabPanesInternal() {
        if (this.props.mode === EditMode.ADD) {
            return null;
        } else {
            return this.getExtraTabPanes();
        }
    }
}